import {
  push,
  query,
  orderByChild,
  equalTo,
  update,
  runTransaction,
  ref,
  set,
  get,
  remove,
} from "firebase/database";
import { storage, db } from "./firebase";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { verifyAuth, subscriptionCheckAction } from "./authFunction";
import { isFormActive, FORM_STATUS } from "../Constants/formStatus";
import {
  getSubscriptionFormLimit,
  isSubscriptionExpired,
} from "../Constants/subscription";

const FORMS_PATH = "contacthq/forms";
const RESPONSES_PATH = "contacthq/responses";

const countOtherActiveForms = async (companyId, excludeFormId) => {
  const allSnapshot = await get(
    query(ref(db, FORMS_PATH), orderByChild("companyId"), equalTo(companyId))
  );
  let activeCount = 0;
  if (allSnapshot.exists()) {
    allSnapshot.forEach((c) => {
      if (c.key !== excludeFormId && isFormActive(c.val().status)) {
        activeCount++;
      }
    });
  }
  return activeCount;
};

/**
 * Enforce subscription + active-form limit when saving a form as published/active.
 */
const assertCanPublish = async (companyData, formId, nextStatus) => {
  if (!isFormActive(nextStatus)) return { ok: true };

  const sub = companyData.subscription || {};
  if (isSubscriptionExpired(sub)) {
    await subscriptionCheckAction(companyData);
    return {
      ok: false,
      error: "Subscription expired. Please renew to publish forms.",
    };
  }

  const limit = getSubscriptionFormLimit(sub);
  const activeCount = await countOtherActiveForms(
    companyData.companyId,
    formId
  );
  if (activeCount >= limit) {
    return {
      ok: false,
      error: `Limit reached (${activeCount}/${limit}). Upgrade your plan to publish more active forms.`,
    };
  }
  return { ok: true };
};

export const saveFormToFirebase = async (formData) => {
  try {
    const auth = await verifyAuth();
    if (!auth.success) return { success: false, error: "Unauthorized" };

    const formId = formData.id;
    if (!formId) return { success: false, error: "Missing form id" };

    const existingSnap = await get(ref(db, `${FORMS_PATH}/${formId}`));
    const existing = existingSnap.exists() ? existingSnap.val() : null;

    if (existing && existing.companyId !== auth.companyData.companyId) {
      return { success: false, error: "Unauthorized ownership" };
    }

    const nextStatus = formData.status;
    const wasActive = existing ? isFormActive(existing.status) : false;
    const willBeActive = isFormActive(nextStatus);

    // Only gate when becoming (or remaining) active via this write
    if (willBeActive && !wasActive) {
      const gate = await assertCanPublish(
        auth.companyData,
        formId,
        nextStatus
      );
      if (!gate.ok) return { success: false, error: gate.error };
    } else if (willBeActive && wasActive) {
      // Already active — allow updates; still block if subscription expired
      if (isSubscriptionExpired(auth.companyData.subscription)) {
        await subscriptionCheckAction(auth.companyData);
        return {
          success: false,
          error: "Subscription expired. Please renew to keep forms active.",
        };
      }
    }

    const formRef = ref(db, `${FORMS_PATH}/${formId}`);
    const dataToSave = {
      ...formData,
      companyId: auth.companyData.companyId,
      lastUpdated: new Date().toISOString(),
    };

    // Preserve createdAt on updates
    if (existing?.createdAt) {
      dataToSave.createdAt = existing.createdAt;
    } else if (!dataToSave.createdAt) {
      dataToSave.createdAt = new Date().toISOString();
    }

    await set(formRef, dataToSave);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getFormFromFirebase = async (formId) => {
  try {
    const snapshot = await get(ref(db, `${FORMS_PATH}/${formId}`));
    return snapshot.exists()
      ? { success: true, data: snapshot.val() }
      : { success: false, message: "Form not found" };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const incrementFormViews = async (formId) => {
  try {
    const viewsRef = ref(db, `${FORMS_PATH}/${formId}/views`);
    await runTransaction(viewsRef, (curr) => (curr || 0) + 1);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const fetchPublicForm = async (formId) => {
  const snapshot = await get(ref(db, `${FORMS_PATH}/${formId}`));
  if (!snapshot.exists()) throw new Error("Form not found.");
  const data = snapshot.val();
  if (isFormActive(data.status)) {
    return data;
  }
  throw new Error("This form is currently private or inactive.");
};

export const incrementSubmissionCount = async (formId) => {
  try {
    const countRef = ref(db, `${FORMS_PATH}/${formId}/response`);
    await runTransaction(countRef, (curr) => (curr || 0) + 1);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const submitFormResponse = async (formId, answers, content) => {
  try {
    const processedAnswers = { ...answers };
    const uploadPromises = Object.entries(answers).map(async ([key, data]) => {
      const file = data?.value;
      if (file instanceof File) {
        const fileRef = storageRef(
          storage,
          `${RESPONSES_PATH}/${formId}/${Date.now()}_${file.name}`
        );
        await uploadBytes(fileRef, file);
        processedAnswers[key] = {
          ...data,
          value: await getDownloadURL(fileRef),
        };
      }
    });

    await Promise.all(uploadPromises);

    const newResponseRef = push(ref(db, `${RESPONSES_PATH}/${formId}`));
    const payload = {
      content,
      answers: processedAnswers,
      submittedAt: new Date().toISOString(),
      status: "Unseen",
      priority: "Normal",
      isStarred: false,
      isArchived: false,
      metadata: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        referrer: document.referrer || "direct",
        deviceType: /Mobi|Android/i.test(navigator.userAgent)
          ? "Mobile"
          : "Desktop",
      },
    };

    await Promise.all([
      set(newResponseRef, payload),
      incrementSubmissionCount(formId),
    ]);

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getAllForms = async () => {
  try {
    const auth = await verifyAuth();
    if (!auth.success) return { success: false, error: "Unauthorized" };

    const { companyId, subscription = {} } = auth.companyData;
    const formsSnapshot = await get(
      query(ref(db, FORMS_PATH), orderByChild("companyId"), equalTo(companyId))
    );

    if (!formsSnapshot.exists()) return { success: true, data: [] };

    const data = formsSnapshot.val();
    let formsArray = Object.entries(data).map(([id, val]) => ({ ...val, id }));

    const now = new Date();
    const expiresAt = subscription.expiresAt;
    const formLimit = getSubscriptionFormLimit(subscription);
    const activeForms = formsArray.filter((f) => isFormActive(f.status));

    const updates = {};
    let triggered = false;

    if (expiresAt && new Date(expiresAt) < now) {
      activeForms.forEach((f) => {
        updates[`${FORMS_PATH}/${f.id}/status`] = FORM_STATUS.PAUSED;
        updates[`${FORMS_PATH}/${f.id}/lastUpdated`] = now.toISOString();
        f.status = FORM_STATUS.PAUSED;
      });
      triggered = true;
    } else if (activeForms.length > formLimit) {
      activeForms.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      activeForms.slice(formLimit).forEach((f) => {
        updates[`${FORMS_PATH}/${f.id}/status`] = FORM_STATUS.PAUSED;
        updates[`${FORMS_PATH}/${f.id}/lastUpdated`] = now.toISOString();
        f.status = FORM_STATUS.PAUSED;
      });
      triggered = true;
    }
    if (triggered) await update(ref(db), updates);
    return {
      success: true,
      data: formsArray.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      ),
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteForm = async (formId) => {
  try {
    const auth = await verifyAuth();
    if (!auth.success) return { success: false, error: "Unauthorized" };

    const formRef = ref(db, `${FORMS_PATH}/${formId}`);
    const snap = await get(formRef);
    if (!snap.exists()) return { success: false, error: "Not found" };

    if (snap.val().companyId !== auth.companyData.companyId) {
      return { success: false, error: "Unauthorized ownership" };
    }

    await Promise.all([
      remove(formRef),
      remove(ref(db, `${RESPONSES_PATH}/${formId}`)),
    ]);

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateFormSettings = async (formId, updatedData) => {
  try {
    const auth = await verifyAuth();
    if (!auth.success) return { success: false, error: "Unauthorized" };

    if (updatedData?.status && isFormActive(updatedData.status)) {
      const gate = await assertCanPublish(
        auth.companyData,
        formId,
        updatedData.status
      );
      // Allow if form is already active
      const snap = await get(ref(db, `${FORMS_PATH}/${formId}`));
      const alreadyActive = snap.exists() && isFormActive(snap.val().status);
      if (!alreadyActive && !gate.ok) {
        return { success: false, error: gate.error };
      }
    }

    const cleanData = JSON.parse(JSON.stringify(updatedData));
    await update(ref(db, `${FORMS_PATH}/${formId}`), {
      ...cleanData,
      updatedAt: new Date().toISOString(),
    });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const toggleFormStatus = async (formId) => {
  try {
    const auth = await verifyAuth();
    if (!auth.success) return { success: false, error: "Unauthorized" };

    const { companyData } = auth;
    const sub = companyData.subscription || {};

    if (isSubscriptionExpired(sub)) {
      await subscriptionCheckAction(companyData);
      return { success: false, error: "Subscription expired." };
    }

    const formRef = ref(db, `${FORMS_PATH}/${formId}`);
    const snap = await get(formRef);
    if (!snap.exists()) return { success: false, error: "Not found" };

    const formData = snap.val();
    if (formData.companyId !== companyData.companyId)
      return { success: false, error: "Unauthorized" };

    const currentStatus = formData.status;
    const newStatus = isFormActive(currentStatus)
      ? FORM_STATUS.PAUSED
      : FORM_STATUS.PUBLISHED;

    if (newStatus === FORM_STATUS.PUBLISHED) {
      const gate = await assertCanPublish(companyData, formId, newStatus);
      if (!gate.ok) return { success: false, error: gate.error };
    }

    await update(formRef, {
      status: newStatus,
      lastUpdated: new Date().toISOString(),
    });
    return { success: true, status: newStatus };
  } catch (error) {
    return { success: false, error: error.message };
  }
};
